import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Form, Input } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { patientLogin } from '../../http/patientPortal';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { useI18n } from '../../shared/i18n';
import {
  PatientLoginCard,
  PatientLoginContainer,
  PatientLoginHeader,
  PatientLoginBody,
} from './styles';

interface LoginFormValues {
  organization_code?: string;
  cpf_cnpj: string;
  birthday: dayjs.Dayjs;
}

const formatCpfMask = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const PatientLogin: React.FC = () => {
  const [form] = Form.useForm<LoginFormValues>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useI18n();
  const { tenant } = useParams<{ tenant?: string }>();
  const navigate = useNavigate();

  const storedTenant = sessionStorage.getItem('@Sigeclin:patientTenant') ?? '';
  const resolvedTenantFromUrl = tenant?.trim() ?? '';
  const hasTenantInUrl = Boolean(resolvedTenantFromUrl);

  const initialOrganizationCode = useMemo(() => {
    if (resolvedTenantFromUrl) return resolvedTenantFromUrl;
    return storedTenant;
  }, [resolvedTenantFromUrl, storedTenant]);

  useEffect(() => {
    if (initialOrganizationCode) {
      form.setFieldsValue({ organization_code: initialOrganizationCode });
    }
  }, [form, initialOrganizationCode]);

  const handleSubmit = async (values: LoginFormValues) => {
    setError(null);
    setLoading(true);
    try {
      const organizationCode =
        resolvedTenantFromUrl ||
        values.organization_code?.trim() ||
        storedTenant;

      if (!organizationCode) {
        setError(t('patientLogin.tenant.missing'));
        setLoading(false);
        return;
      }

      const cpfRaw = values.cpf_cnpj.replace(/\D/g, '');
      const birthday = values.birthday.format('YYYY-MM-DD');

      const { client, access_token } = await patientLogin(organizationCode, {
        cpf_cnpj: cpfRaw,
        birthday,
      });

      sessionStorage.setItem('@Sigeclin:patientToken', access_token);
      sessionStorage.setItem('@Sigeclin:patientName', client.corporate_name);
      sessionStorage.setItem('@Sigeclin:patientTenant', organizationCode);

      const dashboardPath = `/${organizationCode}/portal/painel`;
      navigate(dashboardPath, { replace: true });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ?? t('patientLogin.errorDefault');
      setError(Array.isArray(msg) ? msg[0] : msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PatientLoginContainer>
      <PatientLoginCard>
        <PatientLoginHeader>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              width: '100%',
            }}
          >
            <div className="clinic-name">{t('patientLogin.portalTitle')}</div>
            <LanguageSwitcher compact />
          </div>
          <div className="portal-icon">
            <UserOutlined />
          </div>
          <h1>{t('patientLogin.heading')}</h1>
          <p>{t('patientLogin.subtitle')}</p>
        </PatientLoginHeader>

        <PatientLoginBody>
          {error && (
            <Alert
              type="error"
              message={error}
              showIcon
              style={{ marginBottom: 20, borderRadius: 10 }}
            />
          )}

          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            {!hasTenantInUrl && (
              <Form.Item
                name="organization_code"
                label={t('patientLogin.tenant.label')}
                extra={t('patientLogin.tenant.extra')}
                rules={[
                  {
                    required: true,
                    message: t('patientLogin.tenant.required'),
                  },
                ]}
              >
                <Input
                  placeholder={t('patientLogin.tenant.placeholder')}
                  size="large"
                />
              </Form.Item>
            )}

            <Form.Item
              name="cpf_cnpj"
              label={t('patientLogin.cpf.label')}
              rules={[
                { required: true, message: t('patientLogin.cpf.required') },
                {
                  validator: (_, value) => {
                    const digits = (value ?? '').replace(/\D/g, '');
                    if (digits.length < 11) {
                      return Promise.reject(
                        new Error(t('patientLogin.cpf.invalid')),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              getValueFromEvent={(e) => formatCpfMask(e.target.value)}
            >
              <Input
                placeholder={t('patientLogin.cpf.placeholder')}
                maxLength={14}
                size="large"
                inputMode="numeric"
              />
            </Form.Item>

            <Form.Item
              name="birthday"
              label={t('patientLogin.birthday.label')}
              rules={[
                {
                  required: true,
                  message: t('patientLogin.birthday.required'),
                },
              ]}
            >
              <DatePicker
                format="DD/MM/YYYY"
                placeholder={t('patientLogin.birthday.placeholder')}
                size="large"
                style={{ width: '100%' }}
                inputReadOnly={false}
                disabledDate={(d) => d && d.isAfter(dayjs())}
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className="submit-btn"
              size="large"
            >
              {t('patientLogin.submit')}
            </Button>
          </Form>

          <p className="hint-text">
            {t('patientLogin.hint')
              .split('\n')
              .map((line, index) => (
                <React.Fragment key={line}>
                  {index > 0 ? <br /> : null}
                  {line}
                </React.Fragment>
              ))}
          </p>
        </PatientLoginBody>
      </PatientLoginCard>
    </PatientLoginContainer>
  );
};

export default PatientLogin;
